import { Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { ControlValueAccessor } from "@angular/forms";
import { delayHelper } from "@commonHelpers/delay-helper";
import { isNullish } from "@commonHelpers/math-utils";
import { KeyValue } from "@commonInterfaces/iKeyValue";
import { NgbDropdown } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from "rxjs";

// WICHTIG: Bei ControlValueAccessor Komponenten click Events vermeiden. u.A. kann es dann im Form dazu kommen das bei Click auf Enter in einen anderen Feld Click events ausgelöst werden
// Stattdessen Mousedown und Mouseup events verwenden.
@Directive()
export abstract class BasicControlValueAccessor implements ControlValueAccessor, OnChanges, OnDestroy {

	@Input() list?: KeyValue[];
	@Input() disabledOptions?: number[];
	@Input() dropdownHeight?: string;
	@Input() maxDropdownHeight?: string;
	@Input() minHeightDropdown?: number;
	@Input() isBody = false;
	@Input() placeholder = '';
	@ViewChild("dropdown") dropdown?: NgbDropdown;
	@Output() changeOpen = new EventEmitter<boolean>()
	@Output() blur = new EventEmitter()
	public model: any;
	public title = "";
	protected inputmode = false;
	protected allowCustom = false;
	private onTouchedFunctions: any[] = [];
	private onChangeFunctions: any[] = [];
	private openChangeSubscription?: Subscription;
	public disabled = false;
	private blurFunction = () => {
		if (this.disabled) {
			return;
		}
		this.onTouchedFunctions.forEach((fn: any) => fn());
	}

	private mouseUpFunction = (event: MouseEvent) => this.dropdownToggle(this.dropdown, event);
	private keydownFunction = (event: KeyboardEvent) => {
		this.navigate(event, this.dropdown)
	}
	constructor(protected elRef: ElementRef) {

	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['list']?.currentValue && !isNullish(this.model)) {
			this.title = this.list?.find(item => item.value === this.model)?.key ?? '';
			if (this.allowCustom && this.title === '') {
				this.title = isNullish(this.model) ? '' : this.model;
			}
			this.initCustomList();
		}
	}

	public registerOnChange(fn: any): void {
		this.onChangeFunctions.push(fn);
	}

	public registerOnTouched(fn: any): void {
		this.onTouchedFunctions.push(fn);
	}

	public setDisabledState?(isDisabled: boolean): void {
		if (isDisabled) {
			this.elRef.nativeElement.setAttribute('tabindex', -1);
			this.elRef.nativeElement.style.pointerEvents = 'none';
		} else {
			this.elRef.nativeElement.setAttribute('tabindex', 0);
			this.elRef.nativeElement.style.pointerEvents = 'all';
		}
		this.disabled = isDisabled;
	}

	public writeValue(obj: any): void {
		this.model = obj
		if (!isNullish(this.model)) {
			this.title = this.list?.find(item => item.value === this.model)?.key ?? '';
			if (this.allowCustom && this.title === '') {
				this.title = isNullish(this.model) ? '' : this.model;
			}

			this.initCustomList();
		} else {
			this.title = '';
		}
	}

	protected removeEventListener() {
		this.elRef.nativeElement.removeEventListener('blur', this.blurFunction);
		this.elRef.nativeElement.removeEventListener('mouseup', this.mouseUpFunction);
		this.elRef.nativeElement.removeEventListener('keydown', this.keydownFunction);
	}

	private navigate(event?: KeyboardEvent, dropdown?: NgbDropdown) {
		if (isNullish(event) || this.disabled) {
			return;
		}

		if (!this.inputmode && event.key !== 'Tab') {
			event.preventDefault();
		}

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			const idx = this.navigateList()?.findIndex(item => item.value === this.model);
			const newIdx = event.key === 'ArrowDown' ? idx + 1 : idx - 1;
			const newItem = this.navigateList()?.[newIdx];
			if (!isNullish(newItem)) {
				this.valueChange(newItem);
				this.scrollIntoView(newIdx)
			}


		}

		if (event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter') {
			this.dropdownToggle(dropdown);
			return;
		}

	}

	protected scrollIntoView(idx?: number) {
		if (!isNullish(idx) && idx > -1) {
			const scrollbar = this.elRef.nativeElement.querySelector(`.dropdown-menu`);
			const element = this.elRef.nativeElement.querySelector(`.dropdown-item-${idx}`);
			if (!isNullish(scrollbar) && !isNullish(element)) {
				scrollbar.scrollTop = element.offsetTop - scrollbar.clientHeight / 2;
			}
		}
	}

	private dropdownToggle(dropdown?: NgbDropdown, event?: MouseEvent) {
		if (isNullish(dropdown)) {
			return;
		}
		if (this.disabled) {
			return;
		}
		if (dropdown.isOpen()) {
			delayHelper(() => {
				const currentElement = event?.target as Element;
				// Ausnahmenfall: Nutzer navigiert auf scrollbalken ohne das Mausrad zu benutzen
				if (currentElement?.classList?.contains('dropdown-menu')) {
					return;
				}
				dropdown.close();
			});
		} else if (!this.disabled) {
			dropdown.open();
			this.focusOnOpen();
			this.changeOpen.emit(true);
			this.openChangeSubscription ??= dropdown.openChange.subscribe(next => this.changeOpen.emit(next));
			delayHelper(() => {
				if (!isNullish(this.model)) {
					const idx = this.list?.findIndex(item => item.value === this.model);
					this.scrollIntoView(idx);

				}
			})
		}
	}

	public valueChange(item: KeyValue) {
		this.model = item.value;
		this.title = item.key;
		this.markForChange();
	}

	protected markForChange() {
		this.onChangeFunctions.forEach(fn => fn(this.model));
		this.onTouchedFunctions.forEach(fn => fn());
	}

	protected init() {
		this.elRef.nativeElement.setAttribute('tabindex', 0);
		this.elRef.nativeElement.addEventListener('blur', this.blurFunction);
		this.elRef.nativeElement.addEventListener('mouseup', this.mouseUpFunction);
		this.elRef.nativeElement.addEventListener('keydown', this.keydownFunction);
	}

	// Kann in Kindklasse überschrieben werden, wenn liste über Suche verändert wird
	protected navigateList(): KeyValue[] {
		return this.list ?? [];
	}

	protected initCustomList() {
	}

	ngOnDestroy(): void {
		this.removeEventListener();
		this.openChangeSubscription?.unsubscribe();
	}

	protected abstract focusOnOpen(): void;

}


